import { useEffect, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { getUsers, postUsersAdmin, deleteContestUserTicks } from '../../utils/api';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Loading from '../loading';

const Users = () => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [users, setUsers] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; userId: number | null; userName: string }>({
    open: false,
    userId: null,
    userName: ''
  });

  function toggleAdmin(id) {
    const newUsers = [...users];
    const userIndex = newUsers.findIndex(u => u.id === id);
    
    if (userIndex !== -1) {
      newUsers[userIndex].isAdmin = !newUsers[userIndex].isAdmin;
      setUsers(newUsers); // Optimistic update

      postUsersAdmin(accessToken, newUsers[userIndex].id, newUsers[userIndex].name, newUsers[userIndex].email, newUsers[userIndex].isAdmin)
      .then(() => {
        getUsers(accessToken).then((data) => {
          setUsers(data);
        })
        .catch((error) => {
          console.warn(error);
          alert(error.toString());
        });
      })
      .catch((error) => {
        console.warn(error);
        alert(error.toString());
      });
    }
  }

  const handleClearTicksClick = (userId: number, userName: string) => {
    setConfirmDialog({ open: true, userId, userName });
  };

  const handleClearTicksConfirm = () => {
    if (confirmDialog.userId) {
      deleteContestUserTicks(accessToken, confirmDialog.userId)
        .then(() => {
          getUsers(accessToken).then((data) => setUsers(data));
        })
        .catch((error) => {
          console.warn(error);
          alert(error.toString());
        });
    }
    setConfirmDialog({ open: false, userId: null, userName: '' });
  };

  const handleClearTicksCancel = () => {
    setConfirmDialog({ open: false, userId: null, userName: '' });
  };

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((token) => setAccessToken(token));
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (!isLoading && accessToken) {
      getUsers(accessToken).then((data) => {
        setUsers(data);
      })
      .catch((error) => {
        console.warn(error);
        alert(error.toString());
      });
    }
  }, [isLoading, accessToken]);

  if (isLoading || !users) {
    return <Loading/>;
  }

  return (
    <Box sx={{
      overflow: 'auto',
      maxHeight: (theme) => `calc(100vh - ${theme.spacing(14)})`,
    }}>
      <Dialog
        open={confirmDialog.open}
        onClose={handleClearTicksCancel}
      >
        <DialogTitle>Clear Contest Ticks</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear all contest ticks for {confirmDialog.userName}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearTicksCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleClearTicksConfirm} color="error" variant="contained">
            Clear Ticks
          </Button>
        </DialogActions>
      </Dialog>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>Name</TableCell>
            <TableCell sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>Ticks</TableCell>
            <TableCell sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>Admin</TableCell>
            <TableCell sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>E-mail</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map(u => {
            return (
              <TableRow key={u.id}>
                <TableCell sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>{u.name}</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                  {(u.numContestTicks > 0) && (
                    <Button 
                      size="small" 
                      color="error" 
                      startIcon={<DeleteIcon />}
                      onClick={() => handleClearTicksClick(u.id, u.name)}
                      sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}
                    >
                      {u.numContestTicks}
                    </Button>
                  )}
                </TableCell>
                <TableCell><Switch disabled={u.isMe} checked={u.isAdmin} value="Admin" color="primary" onChange={() => toggleAdmin(u.id)} /></TableCell>
                <TableCell sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>{u.email}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

export default Users;
